import {
  Button,
  Card,
  DatePicker,
  DateRangePicker,
  Dialog,
  DialogPanel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TextInput,
} from "@tremor/react";
import { ChevronDown, ChevronRight, PhoneCall, Plus, Trash2, X } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { keys } from "../queryKeys.js";
import { toast } from "sonner";
import {
  createEscalationPolicy,
  createOverride,
  createSchedule,
  deleteEscalationPolicy,
  deleteOverride,
  deleteSchedule,
  takeover,
  updateEscalationPolicy,
  updateSchedule,
} from "../api/oncall.js";
import {
  useSchedules,
  useEscalationPolicies,
  useCurrentOnCall,
  useScheduleShifts,
  useCreateRotation,
  useUpdateRotation,
  useDeleteRotation,
} from "../queries/oncall.js";
import { useChannels } from "../queries/channels.js";
import { useMembers } from "../queries/workspaces.js";
import { usePermission } from "../lib/usePermission.js";
import { useIsRoot } from "../lib/useIsRoot.js";

import { useTranslation } from "react-i18next";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function userName(userId, members, t) {
  if (!userId) return t("common.no_one");
  const m = members.find((x) => x.user_id === userId || x.id === userId);
  if (!m) return userId.slice(0, 8) + "…";
  return m.name || m.email || userId.slice(0, 8) + "…";
}

function groupName(groupId, groups) {
  if (!groupId) return groupId;
  const g = groups.find((x) => String(x.id) === String(groupId));
  return g ? g.name : groupId.slice(0, 8) + "…";
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Combine a Date object + "HH:MM" string → ISO string */
function combineDateTime(date, time) {
  if (!date) return null;
  const d = new Date(date);
  const [h, m] = (time || "00:00").split(":").map(Number);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

/** Parse an ISO string into { date: Date, time: "HH:MM" } */
function splitDateTime(iso) {
  if (!iso) return { date: null, time: "00:00" };
  const d = new Date(iso);
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return { date: d, time: `${h}:${m}` };
}

const inputCls =
  "w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600";
const labelCls = "mb-1 block text-xs font-medium text-gray-600";

// ─── Upgrade prompt ───────────────────────────────────────────────────────────

function UpgradePrompt() {
  const { t } = useTranslation("on_call");
  const navigate = useNavigate();
  const isRoot = useIsRoot();
  return (
    <Card className="p-8 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-teal-50">
        <PhoneCall className="h-6 w-6 text-teal-600" />
      </div>
      <h2 className="text-lg font-semibold text-gray-900">{t("upgrade.title")}</h2>
      <p className="mt-2 text-sm text-gray-500">
        {t("upgrade.description")}
      </p>
      {isRoot && (
        <Button className="mt-4" onClick={() => navigate("/billing")}>
          {t("upgrade.button")}
        </Button>
      )}
    </Card>
  );
}

// ─── Current On-Call card ─────────────────────────────────────────────────────

function CurrentOnCallCard({ schedules, members, onTakeover }) {
  const { t } = useTranslation("on_call");
  const qc = useQueryClient();
  const { data: current, isLoading: loading } = useCurrentOnCall();
  const [takingOver, setTakingOver] = useState(null);

  async function handleTakeover(scheduleId) {
    setTakingOver(scheduleId);
    try {
      await takeover(scheduleId);
      toast.success(t("current_card.success_takeover"));
      qc.invalidateQueries({ queryKey: ["oncall", "current"] });
      qc.invalidateQueries({ queryKey: keys.oncall.all });
      if (onTakeover) onTakeover();
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to take over on-call");
    } finally {
      setTakingOver(null);
    }
  }

  const entries = Array.isArray(current) ? current : current ? [current] : [];

  return (
    <Card className="p-0">
      <div className="border-b border-gray-100 px-5 py-4">
        <h2 className="text-sm font-semibold text-gray-700">{t("current_card.title")}</h2>
        <p className="mt-0.5 text-xs text-gray-400">{t("current_card.description")}</p>
      </div>
      {loading ? (
        <p className="px-5 py-6 text-center text-sm text-gray-400">{t("current_card.loading")}</p>
      ) : schedules.length === 0 ? (
        <p className="px-5 py-6 text-center text-sm text-gray-400">{t("current_card.no_schedules")}</p>
      ) : (
        <div className="divide-y divide-gray-50">
          {schedules.map((s) => {
            const entry = entries.find((e) => e.schedule_id === s.id);
            const userId = entry?.user_id ?? null;
            return (
              <div key={s.id} className="flex items-center justify-between gap-4 px-5 py-3">
                <div>
                  <p className="text-sm font-medium text-gray-800">{s.name}</p>
                  <p className="mt-0.5 text-xs text-gray-400">
                    {userId ? (
                      <span>{userName(userId, members, t)}</span>
                    ) : (
                      <span className="text-gray-300">{t("current_card.no_one")}</span>
                    )}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="xs"
                  disabled={takingOver === s.id}
                  onClick={() => handleTakeover(s.id)}
                >
                  {takingOver === s.id ? t("current_card.taking_over") : t("current_card.takeover")}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ─── Schedule shifts inline view ──────────────────────────────────────────────

function ScheduleShifts({ scheduleId, members }) {
  const { t } = useTranslation("on_call");
  const [range] = useState(() => ({
    from: new Date().toISOString(),
    to: new Date(Date.now() + 28 * 24 * 3600 * 1000).toISOString(),
  }));
  const { data, isLoading: loading } = useScheduleShifts(scheduleId, range.from, range.to);
  const shifts = Array.isArray(data) ? data : [];

  if (loading) return <p className="px-4 py-3 text-xs text-gray-400">{t("schedules.table.loading_shifts")}</p>;
  if (shifts.length === 0)
    return <p className="px-4 py-3 text-xs text-gray-400">{t("schedules.table.no_shifts")}</p>;

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t("shifts.who")}</TableHeaderCell>
            <TableHeaderCell>{t("shifts.starts")}</TableHeaderCell>
            <TableHeaderCell>{t("shifts.ends")}</TableHeaderCell>
            <TableHeaderCell>{t("shifts.type")}</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shifts.map((shift, i) => (
            <TableRow key={i}>
              <TableCell className="text-sm text-gray-700">
                {userName(shift.user_id, members, t)}
              </TableCell>
              <TableCell className="text-xs text-gray-500">
                {fmtDate(shift.start_at)}
              </TableCell>
              <TableCell className="text-xs text-gray-500">
                {fmtDate(shift.end_at)}
              </TableCell>
              <TableCell>
                {shift.is_override ? (
                  <span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-700">{t("shifts.override")}</span>
                ) : (
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">{t("shifts.rotation")}</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ─── Override dialog ───────────────────────────────────────────────────────────

function OverrideDialog({ scheduleId, members, onClose, onSaved }) {
  const { t } = useTranslation("on_call");
  const [userId, setUserId] = useState(members[0]?.user_id ?? "");
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("09:00");
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!dateRange.from || !dateRange.to) {
      toast.error(t("override_modal.error_range"));
      return;
    }
    if (!userId) {
      toast.error(t("override_modal.error_user"));
      return;
    }
    setSaving(true);
    try {
      const start_at = combineDateTime(dateRange.from, startTime);
      const end_at = combineDateTime(dateRange.to, endTime);
      const saved = await createOverride(scheduleId, { user_id: userId, start_at, end_at });
      toast.success(t("override_modal.success"));
      onSaved(saved);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to create override");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="max-w-md">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">{t("override_modal.title")}</h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100">
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelCls}>{t("override_modal.user")}</label>
            <select value={userId} onChange={(e) => setUserId(e.target.value)} className={inputCls}>
              <option value="">{t("override_modal.select_user")}</option>
              {members.map((m) => (
                <option key={m.user_id ?? m.id} value={m.user_id ?? m.id}>
                  {m.name || m.email}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>{t("override_modal.date_range")}</label>
            <DateRangePicker
              value={dateRange}
              onValueChange={setDateRange}
              enableSelect={false}
              placeholder={t("override_modal.placeholder")}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{t("override_modal.start_time")}</label>
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{t("override_modal.end_time")}</label>
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className={inputCls} />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={onClose}>{t("common:common.cancel")}</Button>
            <Button type="submit" disabled={saving}>{saving ? t("override_modal.creating") : t("override_modal.create")}</Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}

// ─── Schedule dialog ───────────────────────────────────────────────────────────

function ScheduleDialog({ schedule, members, onClose, onSaved }) {
  const { t } = useTranslation("on_call");
  const isEdit = !!schedule;

  const initStartAt = splitDateTime(schedule?.rotation_start_at ?? null);
  const [form, setForm] = useState({
    name: schedule?.name ?? "",
    timezone: schedule?.timezone ?? "UTC",
    rotation_type: schedule?.rotation_type ?? "weekly",
    rotation_duration_hours: schedule?.rotation_duration_hours ?? 168,
    memberIds: (schedule?.members ?? []).map((m) => m.user_id ?? m).filter(Boolean),
  });
  const [startDate, setStartDate] = useState(initStartAt.date);
  const [startTime, setStartTime] = useState(initStartAt.time);
  const [saving, setSaving] = useState(false);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function addMember(uid) {
    if (!uid || form.memberIds.includes(uid)) return;
    setField("memberIds", [...form.memberIds, uid]);
  }

  function removeMember(uid) {
    setField("memberIds", form.memberIds.filter((id) => id !== uid));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        timezone: form.timezone,
        rotation_type: form.rotation_type,
        rotation_duration_hours:
          form.rotation_type === "custom" ? Number(form.rotation_duration_hours) : undefined,
        rotation_start_at: combineDateTime(startDate, startTime),
        members: form.memberIds.map((uid) => ({ user_id: uid })),
      };
      const saved = isEdit
        ? await updateSchedule(schedule.id, payload)
        : await createSchedule(payload);
      toast.success(isEdit ? t("schedule_modal.success_edit") : t("schedule_modal.success_new"));
      onSaved(saved);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to save schedule");
    } finally {
      setSaving(false);
    }
  }

  const availableMembers = members.filter((m) => !form.memberIds.includes(m.user_id ?? m.id));

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-lg flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {isEdit ? t("schedule_modal.title_edit") : t("schedule_modal.title_new")}
            </h2>
            {isEdit && <p className="mt-0.5 text-xs text-gray-400">{schedule.name}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            {/* Name */}
            <div>
              <label className={labelCls}>{t("schedule_modal.name")}</label>
              <TextInput
                required
                value={form.name}
                onValueChange={(v) => setField("name", v)}
                placeholder={t("schedule_modal.name_placeholder")}
              />
            </div>

            {/* Timezone */}
            <div>
              <label className={labelCls}>{t("schedule_modal.timezone")}</label>
              <TextInput
                value={form.timezone}
                onValueChange={(v) => setField("timezone", v)}
                placeholder={t("schedule_modal.timezone_placeholder")}
              />
              <p className="mt-1 text-xs text-gray-400">{t("schedule_modal.timezone_help")}</p>
            </div>

            {/* Rotation type */}
            <div>
              <label className={labelCls}>{t("schedule_modal.rotation_type")}</label>
              <select
                value={form.rotation_type}
                onChange={(e) => setField("rotation_type", e.target.value)}
                className={inputCls}
              >
                <option value="daily">{t("schedules.rotation.daily")}</option>
                <option value="weekly">{t("schedules.rotation.weekly")}</option>
                <option value="custom">{t("schedules.rotation.custom")}</option>
              </select>
            </div>

            {/* Custom duration */}
            {form.rotation_type === "custom" && (
              <div>
                <label className={labelCls}>{t("schedule_modal.duration")}</label>
                <input
                  type="number"
                  min={1}
                  value={form.rotation_duration_hours}
                  onChange={(e) => setField("rotation_duration_hours", Number(e.target.value))}
                  className={inputCls}
                />
              </div>
            )}

            {/* Rotation start */}
            <div>
              <label className={labelCls}>{t("schedule_modal.starts_at")}</label>
              <DatePicker
                value={startDate}
                onValueChange={setStartDate}
                placeholder={t("schedule_modal.date_placeholder")}
              />
              <div className="mt-2">
                <label className={labelCls}>{t("schedule_modal.start_time")}</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>

            {/* Members */}
            <div>
              <label className={labelCls}>{t("schedule_modal.members_title")}</label>
              {form.memberIds.length > 0 && (
                <div className="mb-2 divide-y divide-gray-50 rounded-md border border-gray-200">
                  {form.memberIds.map((uid, idx) => (
                    <div key={uid} className="flex items-center justify-between px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">{idx + 1}.</span>
                        <span className="text-sm text-gray-700">{userName(uid, members, t)}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeMember(uid)}
                        className="text-gray-300 hover:text-red-500"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {availableMembers.length > 0 ? (
                <select
                  value=""
                  onChange={(e) => { if (e.target.value) addMember(e.target.value); }}
                  className={inputCls}
                >
                  <option value="">{t("schedule_modal.add_member")}</option>
                  {availableMembers.map((m) => (
                    <option key={m.user_id ?? m.id} value={m.user_id ?? m.id}>
                      {m.name || m.email}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-xs text-gray-400">{t("schedule_modal.all_added")}</p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <Button variant="secondary" type="button" onClick={onClose}>{t("common:common.cancel")}</Button>
            <Button type="submit" disabled={saving}>
              {saving ? t("common:common.saving") : isEdit ? t("schedule_modal.save") : t("schedule_modal.create")}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}

// ─── Rotations ────────────────────────────────────────────────────────────────

const DAYS_OF_WEEK = [0, 1, 2, 3, 4, 5, 6]; // 0=Monday..6=Sunday

function RotationDialog({ scheduleId, rotation, members, onClose }) {
  const { t } = useTranslation("on_call");
  const isEdit = !!rotation;
  const createRotation = useCreateRotation();
  const updateRotation = useUpdateRotation();
  const saving = createRotation.isPending || updateRotation.isPending;

  const initStartAt = splitDateTime(rotation?.rotation_start_at ?? null);
  const [form, setForm] = useState({
    name: rotation?.name ?? "",
    position: rotation?.position ?? 0,
    is_active: rotation?.is_active ?? true,
    rotation_type: rotation?.rotation_type ?? "weekly",
    rotation_duration_hours: rotation?.rotation_duration_hours ?? 168,
    memberIds: (rotation?.members ?? []).map((m) => m.user_id).filter(Boolean),
    restrictDays: rotation?.days_of_week != null,
    days_of_week: rotation?.days_of_week ?? [0, 1, 2, 3, 4],
    restrictTime: !!(rotation?.time_start || rotation?.time_end),
    time_start: rotation?.time_start ?? "09:00",
    time_end: rotation?.time_end ?? "17:00",
  });
  const [startDate, setStartDate] = useState(initStartAt.date);
  const [startTime, setStartTime] = useState(initStartAt.time);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function toggleDay(day) {
    setField(
      "days_of_week",
      form.days_of_week.includes(day)
        ? form.days_of_week.filter((d) => d !== day)
        : [...form.days_of_week, day].sort()
    );
  }

  function addMember(uid) {
    if (!uid || form.memberIds.includes(uid)) return;
    setField("memberIds", [...form.memberIds, uid]);
  }
  function removeMember(uid) {
    setField("memberIds", form.memberIds.filter((id) => id !== uid));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const payload = {
      name: form.name,
      position: Number(form.position) || 0,
      is_active: form.is_active,
      rotation_type: form.rotation_type,
      rotation_duration_hours:
        form.rotation_type === "custom" ? Number(form.rotation_duration_hours) : undefined,
      rotation_start_at: combineDateTime(startDate, startTime),
      days_of_week: form.restrictDays ? form.days_of_week : null,
      time_start: form.restrictTime ? form.time_start : null,
      time_end: form.restrictTime ? form.time_end : null,
      members: form.memberIds.map((uid) => ({ user_id: uid })),
    };
    try {
      if (isEdit) {
        await updateRotation.mutateAsync({ scheduleId, rotationId: rotation.id, data: payload });
        toast.success(t("rotation_modal.success_edit"));
      } else {
        await createRotation.mutateAsync({ scheduleId, data: payload });
        toast.success(t("rotation_modal.success_new"));
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error ?? t("rotation_modal.error_save"));
    }
  }

  const availableMembers = members.filter((m) => !form.memberIds.includes(m.user_id ?? m.id));

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-lg flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? t("rotation_modal.title_edit") : t("rotation_modal.title_new")}
          </h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            <div>
              <label className={labelCls}>{t("rotation_modal.name")}</label>
              <TextInput required value={form.name} onValueChange={(v) => setField("name", v)} placeholder={t("rotation_modal.name_placeholder")} />
            </div>

            <div>
              <label className={labelCls}>{t("rotation_modal.priority")}</label>
              <input
                type="number"
                value={form.position}
                onChange={(e) => setField("position", e.target.value)}
                className={inputCls}
              />
              <p className="mt-1 text-xs text-gray-400">{t("rotation_modal.priority_help")}</p>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setField("is_active", e.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
              />
              {t("rotation_modal.active")}
            </label>

            <div>
              <label className={labelCls}>{t("schedule_modal.rotation_type")}</label>
              <select value={form.rotation_type} onChange={(e) => setField("rotation_type", e.target.value)} className={inputCls}>
                <option value="daily">{t("schedules.rotation.daily")}</option>
                <option value="weekly">{t("schedules.rotation.weekly")}</option>
                <option value="custom">{t("schedules.rotation.custom")}</option>
              </select>
            </div>

            {form.rotation_type === "custom" && (
              <div>
                <label className={labelCls}>{t("schedule_modal.duration")}</label>
                <input
                  type="number"
                  min={1}
                  value={form.rotation_duration_hours}
                  onChange={(e) => setField("rotation_duration_hours", Number(e.target.value))}
                  className={inputCls}
                />
              </div>
            )}

            <div>
              <label className={labelCls}>{t("schedule_modal.starts_at")}</label>
              <DatePicker value={startDate} onValueChange={setStartDate} placeholder={t("schedule_modal.date_placeholder")} />
              <div className="mt-2">
                <label className={labelCls}>{t("schedule_modal.start_time")}</label>
                <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className={inputCls} />
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.restrictDays}
                  onChange={(e) => setField("restrictDays", e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
                />
                {t("rotation_modal.restrict_days")}
              </label>
              {form.restrictDays && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {DAYS_OF_WEEK.map((day) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDay(day)}
                      className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                        form.days_of_week.includes(day)
                          ? "bg-teal-600 text-white"
                          : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                      }`}
                    >
                      {t(`rotation_modal.day_short.${day}`)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.restrictTime}
                  onChange={(e) => setField("restrictTime", e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
                />
                {t("rotation_modal.restrict_time")}
              </label>
              {form.restrictTime && (
                <div className="mt-2 flex items-center gap-2">
                  <input type="time" value={form.time_start} onChange={(e) => setField("time_start", e.target.value)} className={inputCls} />
                  <span className="text-xs text-gray-400">{t("rotation_modal.to")}</span>
                  <input type="time" value={form.time_end} onChange={(e) => setField("time_end", e.target.value)} className={inputCls} />
                </div>
              )}
              <p className="mt-1 text-xs text-gray-400">{t("rotation_modal.time_help")}</p>
            </div>

            <div>
              <label className={labelCls}>{t("schedule_modal.members_title")}</label>
              {form.memberIds.length > 0 && (
                <div className="mb-2 divide-y divide-gray-50 rounded-md border border-gray-200">
                  {form.memberIds.map((uid, idx) => (
                    <div key={uid} className="flex items-center justify-between px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">{idx + 1}.</span>
                        <span className="text-sm text-gray-700">{userName(uid, members, t)}</span>
                      </div>
                      <button type="button" onClick={() => removeMember(uid)} className="text-gray-300 hover:text-red-500">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {availableMembers.length > 0 ? (
                <select value="" onChange={(e) => { if (e.target.value) addMember(e.target.value); }} className={inputCls}>
                  <option value="">{t("schedule_modal.add_member")}</option>
                  {availableMembers.map((m) => (
                    <option key={m.user_id ?? m.id} value={m.user_id ?? m.id}>{m.name || m.email}</option>
                  ))}
                </select>
              ) : (
                <p className="text-xs text-gray-400">{t("schedule_modal.all_added")}</p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <Button variant="secondary" type="button" onClick={onClose}>{t("common:common.cancel")}</Button>
            <Button type="submit" disabled={saving}>
              {saving ? t("common:common.saving") : isEdit ? t("rotation_modal.save") : t("rotation_modal.create")}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}

function RotationsPanel({ scheduleId, rotations, members, can }) {
  const { t } = useTranslation("on_call");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const deleteRotation = useDeleteRotation();

  function openCreate() { setEditing(null); setDialogOpen(true); }
  function openEdit(r) { setEditing(r); setDialogOpen(true); }

  async function handleDelete(rotation) {
    if (!window.confirm(t("rotation_modal.delete_confirm", { name: rotation.name }))) return;
    try {
      await deleteRotation.mutateAsync({ scheduleId, rotationId: rotation.id });
      toast.success(t("rotation_modal.success_delete"));
    } catch {
      toast.error(t("rotation_modal.error_delete"));
    }
  }

  const sorted = [...rotations].sort((a, b) => a.position - b.position);

  return (
    <div className="mb-4">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{t("rotation_modal.section_title")}</p>
        {can("on_call.manage") && (
          <Button size="xs" variant="secondary" icon={Plus} onClick={openCreate}>{t("rotation_modal.new")}</Button>
        )}
      </div>
      {sorted.length === 0 ? (
        <p className="text-xs text-gray-400">{t("rotation_modal.empty")}</p>
      ) : (
        <div className="space-y-2">
          {sorted.map((r) => (
            <div key={r.id} className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2">
              <span className="font-mono text-xs text-gray-300">#{r.position}</span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{r.name}</span>
                  {!r.is_active && (
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">{t("rotation_modal.inactive")}</span>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-gray-400">
                  {r.days_of_week
                    ? r.days_of_week.map((d) => t(`rotation_modal.day_short.${d}`)).join("/")
                    : t("rotation_modal.every_day")}
                  {" · "}
                  {r.time_start && r.time_end ? `${r.time_start}-${r.time_end}` : t("rotation_modal.all_day")}
                  {" · "}
                  {(r.members ?? []).map((m) => userName(m.user_id, members, t)).join(", ") || t("rotation_modal.no_members")}
                </p>
              </div>
              {can("on_call.manage") && (
                <div className="flex items-center gap-1.5">
                  <Button variant="secondary" size="xs" onClick={() => openEdit(r)}>{t("schedules.table.edit")}</Button>
                  <Button variant="secondary" size="xs" color="red" icon={Trash2} onClick={() => handleDelete(r)} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {dialogOpen && (
        <RotationDialog scheduleId={scheduleId} rotation={editing} members={members} onClose={() => setDialogOpen(false)} />
      )}
    </div>
  );
}

// ─── Escalation policy step builder ───────────────────────────────────────────

const EMPTY_STEP = () => ({ delay_minutes: 5, targets: [{ type: "user", id: "" }] });
const EMPTY_POLICY_FORM = { name: "", repeat_count: 3, steps: [EMPTY_STEP()] };

/** A single target row inside a step */
function TargetRow({ target, members, groups, schedules, onUpdate, onRemove, showRemove }) {
  const { t } = useTranslation("on_call");
  function resolvedOptions(type) {
    if (type === "user") return members.map((m) => ({ value: m.user_id ?? m.id, label: m.name || m.email }));
    if (type === "contact_group") return groups.map((g) => ({ value: String(g.id), label: g.name }));
    if (type === "schedule") return schedules.map((s) => ({ value: s.id, label: s.name }));
    return [];
  }

  const options = resolvedOptions(target.type);

  return (
    <div className="flex items-center gap-2">
      <select
        value={target.type}
        onChange={(e) => onUpdate({ type: e.target.value, id: "" })}
        className="w-36 rounded-md border border-gray-300 px-2 py-1.5 text-xs outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
      >
        <option value="user">{t("policy_modal.target_user")}</option>
        <option value="contact_group">{t("policy_modal.target_group")}</option>
        <option value="schedule">{t("policy_modal.target_schedule")}</option>
      </select>
      <select
        value={target.id}
        onChange={(e) => onUpdate({ id: e.target.value })}
        className="flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-xs outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
      >
        <option value="">{t("policy_modal.select")}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {showRemove && (
        <button type="button" onClick={onRemove} className="text-gray-300 hover:text-red-500">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function EscalationPolicyDialog({ policy, members, groups, schedules, onClose, onSaved }) {
  const { t } = useTranslation("on_call");
  const isEdit = !!policy;
  const [form, setForm] = useState(
    isEdit
      ? {
          name: policy.name ?? "",
          repeat_count: policy.repeat_count ?? 3,
          steps:
            policy.steps?.length > 0
              ? policy.steps.map((s) => ({
                  delay_minutes: s.delay_minutes ?? 5,
                  targets: s.targets?.length > 0 ? s.targets : [{ type: "user", id: "" }],
                }))
              : [EMPTY_STEP()],
        }
      : { ...EMPTY_POLICY_FORM, steps: [EMPTY_STEP()] }
  );
  const [saving, setSaving] = useState(false);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function addStep() { setField("steps", [...form.steps, EMPTY_STEP()]); }
  function removeStep(idx) { setField("steps", form.steps.filter((_, i) => i !== idx)); }
  function updateStep(idx, key, value) {
    setField("steps", form.steps.map((s, i) => (i === idx ? { ...s, [key]: value } : s)));
  }
  function addTarget(stepIdx) {
    setField("steps", form.steps.map((s, i) =>
      i === stepIdx ? { ...s, targets: [...s.targets, { type: "user", id: "" }] } : s
    ));
  }
  function removeTarget(stepIdx, targetIdx) {
    setField("steps", form.steps.map((s, i) =>
      i === stepIdx ? { ...s, targets: s.targets.filter((_, ti) => ti !== targetIdx) } : s
    ));
  }
  function updateTarget(stepIdx, targetIdx, changes) {
    setField("steps", form.steps.map((s, i) =>
      i === stepIdx
        ? { ...s, targets: s.targets.map((t, ti) => (ti === targetIdx ? { ...t, ...changes } : t)) }
        : s
    ));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        repeat_count: Number(form.repeat_count),
        steps: form.steps.map((s, idx) => ({
          step_number: idx + 1,
          delay_minutes: Number(s.delay_minutes),
          targets: s.targets.filter((t) => t.id),
        })),
      };
      const saved = isEdit
        ? await updateEscalationPolicy(policy.id, payload)
        : await createEscalationPolicy(payload);
      toast.success(isEdit ? t("policy_modal.success_edit") : t("policy_modal.success_new"));
      onSaved(saved);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to save policy");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-xl flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {isEdit ? t("policy_modal.title_edit") : t("policy_modal.title_new")}
            </h2>
            {isEdit && <p className="mt-0.5 text-xs text-gray-400">{policy.name}</p>}
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            <div>
              <label className={labelCls}>{t("policy_modal.name")}</label>
              <TextInput
                required
                value={form.name}
                onValueChange={(v) => setField("name", v)}
                placeholder={t("policy_modal.name_placeholder")}
              />
            </div>

            <div>
              <label className={labelCls}>{t("policy_modal.repeat")}</label>
              <input
                type="number"
                min={1}
                value={form.repeat_count}
                onChange={(e) => setField("repeat_count", Number(e.target.value))}
                className={inputCls}
              />
              <p className="mt-1 text-xs text-gray-400">
                {t("policy_modal.repeat_help")}
              </p>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className={labelCls}>{t("policy_modal.steps_title")}</label>
                <Button type="button" variant="secondary" size="xs" icon={Plus} onClick={addStep}>
                  {t("policy_modal.add_step")}
                </Button>
              </div>

              <div className="space-y-3">
                {form.steps.map((step, stepIdx) => (
                  <div key={stepIdx} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-xs font-semibold text-gray-500">
                        {t("policy_modal.step_label", { count: stepIdx + 1 })}
                        {stepIdx === 0 && (
                          <span className="ml-2 font-normal text-gray-400">{t("policy_modal.immediate")}</span>
                        )}
                      </p>
                      {form.steps.length > 1 && (
                        <button type="button" onClick={() => removeStep(stepIdx)} className="text-gray-300 hover:text-red-500">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>

                    {stepIdx > 0 && (
                      <div className="mb-3">
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          {t("policy_modal.delay")}
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={step.delay_minutes}
                          onChange={(e) => updateStep(stepIdx, "delay_minutes", Number(e.target.value))}
                          className={inputCls}
                        />
                      </div>
                    )}

                    <div>
                      <div className="mb-1 flex items-center justify-between">
                        <label className="text-xs font-medium text-gray-600">{t("policy_modal.notify")}</label>
                        <button
                          type="button"
                          onClick={() => addTarget(stepIdx)}
                          className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700"
                        >
                          <Plus className="h-3 w-3" />
                          {t("policy_modal.add_target")}
                        </button>
                      </div>
                      <div className="space-y-2">
                        {step.targets.map((target, targetIdx) => (
                          <TargetRow
                            key={targetIdx}
                            target={target}
                            stepIdx={stepIdx}
                            targetIdx={targetIdx}
                            members={members}
                            groups={groups}
                            schedules={schedules}
                            onUpdate={(changes) => updateTarget(stepIdx, targetIdx, changes)}
                            onRemove={() => removeTarget(stepIdx, targetIdx)}
                            showRemove={step.targets.length > 1}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <Button variant="secondary" type="button" onClick={onClose}>{t("common:common.cancel")}</Button>
            <Button type="submit" disabled={saving}>
              {saving ? t("common:common.saving") : isEdit ? t("policy_modal.save") : t("policy_modal.create")}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}

// ─── Schedules section ────────────────────────────────────────────────────────

function SchedulesSection({ schedules, loading, members, can }) {
  const { t } = useTranslation(["on_call", "common"]);
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [overrideScheduleId, setOverrideScheduleId] = useState(null);

  function openCreate() { setEditing(null); setDialogOpen(true); }
  function openEdit(s) { setEditing(s); setDialogOpen(true); }

  function handleSaved() {
    qc.invalidateQueries({ queryKey: keys.oncall.all });
  }

  async function handleDelete(id, name) {
    if (!window.confirm(t("schedules.table.delete_confirm", { name }))) return;
    setDeleting(id);
    try {
      await deleteSchedule(id);
      qc.invalidateQueries({ queryKey: keys.oncall.all });
      toast.success(t("schedules.table.success_delete"));
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to delete schedule");
    } finally {
      setDeleting(null);
    }
  }

  return (
    <>
      <Card className="p-0">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-700">{t("schedules.title")}</h2>
            <p className="mt-0.5 text-xs text-gray-400">{t("schedules.description")}</p>
          </div>
          {can("on_call.manage") && (
            <Button size="sm" icon={Plus} onClick={openCreate}>{t("schedules.new")}</Button>
          )}
        </div>

        {loading ? (
          <p className="px-5 py-8 text-center text-sm text-gray-400">{t("schedules.loading")}</p>
        ) : schedules.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-gray-400">
            {t("schedules.empty")}{" "}
            {can("on_call.manage") && (
              <button onClick={openCreate} className="text-teal-600 hover:underline">{t("schedules.create_one")}</button>
            )}
          </p>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell className="w-8" />
                <TableHeaderCell>{t("schedules.table.name")}</TableHeaderCell>
                <TableHeaderCell>{t("schedules.table.timezone")}</TableHeaderCell>
                <TableHeaderCell>{t("schedules.table.rotation")}</TableHeaderCell>
                <TableHeaderCell>{t("schedules.table.members")}</TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.map((s) => (
                <Fragment key={s.id}>
                  <TableRow>
                    <TableCell className="w-8 text-gray-300">
                      <button
                        type="button"
                        onClick={() => setExpandedId((prev) => (prev === s.id ? null : s.id))}
                        className="rounded p-0.5 hover:bg-gray-100"
                      >
                        {expandedId === s.id ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                    </TableCell>
                    <TableCell className="font-medium text-gray-900">
                      <button
                        type="button"
                        onClick={() => setExpandedId((prev) => (prev === s.id ? null : s.id))}
                        className="hover:text-teal-600 hover:underline"
                      >
                        {s.name}
                      </button>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-gray-500">{s.timezone ?? "UTC"}</TableCell>
                    <TableCell className="capitalize text-gray-500">
                      {t(`schedules.rotation.${s.rotation_type ?? "weekly"}`)}
                      {s.rotation_type === "custom" && s.rotation_duration_hours && (
                        <span className="ml-1 text-xs text-gray-400">({t("schedules.rotation.hours", { count: s.rotation_duration_hours })})</span>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {(s.members ?? []).map((m) => userName(m.user_id ?? m, members, t)).join(", ") || "—"}
                    </TableCell>
                    <TableCell>
                      {can("on_call.manage") && (
                        <div className="flex items-center gap-2">
                          <Button variant="secondary" size="xs" onClick={() => setOverrideScheduleId(s.id)}>
                            {t("schedules.table.override")}
                          </Button>
                          <Button variant="secondary" size="xs" onClick={() => openEdit(s)}>{t("schedules.table.edit")}</Button>
                          <Button
                            variant="secondary"
                            size="xs"
                            color="red"
                            icon={Trash2}
                            disabled={deleting === s.id}
                            onClick={() => handleDelete(s.id, s.name)}
                          />
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                  {expandedId === s.id && (
                    <TableRow key={`${s.id}-shifts`}>
                      <TableCell colSpan={6} className="bg-gray-50 p-0">
                        <div className="px-6 py-4">
                          <RotationsPanel
                            scheduleId={s.id}
                            rotations={s.rotations ?? []}
                            members={members}
                            can={can}
                          />
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                            {t("schedules.table.next_weeks")}
                          </p>
                          <ScheduleShifts scheduleId={s.id} members={members} />
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {dialogOpen && (
        <ScheduleDialog
          schedule={editing}
          members={members}
          onClose={() => setDialogOpen(false)}
          onSaved={handleSaved}
        />
      )}
      {overrideScheduleId && (
        <OverrideDialog
          scheduleId={overrideScheduleId}
          members={members}
          onClose={() => setOverrideScheduleId(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["oncall", "shifts"] });
            qc.invalidateQueries({ queryKey: ["oncall", "current"] });
          }}
        />
      )}
    </>
  );
}

// ─── Escalation policies section ──────────────────────────────────────────────

function EscalationPoliciesSection({ members, groups, schedules, can }) {
  const { t } = useTranslation("on_call");
  const qc = useQueryClient();
  const { data: policiesData, isLoading: loading, error } = useEscalationPolicies();
  const policies = policiesData ?? [];
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (error && error.response?.status !== 402) {
      toast.error(error.response?.data?.error ?? "Failed to load escalation policies");
    }
  }, [error]);

  function openCreate() { setEditing(null); setDialogOpen(true); }
  function openEdit(p) { setEditing(p); setDialogOpen(true); }

  function handleSaved() {
    qc.invalidateQueries({ queryKey: keys.oncall.escalationPolicies });
  }

  async function handleDelete(id, name) {
    if (!window.confirm(t("policies.table.delete_confirm", { name }))) return;
    setDeleting(id);
    try {
      await deleteEscalationPolicy(id);
      qc.invalidateQueries({ queryKey: keys.oncall.escalationPolicies });
      toast.success(t("policies.table.success_delete"));
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response?.data?.error ?? t("policies.table.error_in_use"));
      } else {
        toast.error(err.response?.data?.error ?? "Failed to delete policy");
      }
    } finally {
      setDeleting(null);
    }
  }

  return (
    <>
      <Card className="p-0">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-700">{t("policies.title")}</h2>
            <p className="mt-0.5 text-xs text-gray-400">{t("policies.description")}</p>
          </div>
          {can("on_call.manage") && (
            <Button size="sm" icon={Plus} onClick={openCreate}>{t("policies.new")}</Button>
          )}
        </div>

        {loading ? (
          <p className="px-5 py-8 text-center text-sm text-gray-400">{t("policies.loading")}</p>
        ) : policies.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-gray-400">
            {t("policies.empty")}{" "}
            {can("on_call.manage") && (
              <button onClick={openCreate} className="text-teal-600 hover:underline">{t("policies.create_one")}</button>
            )}
          </p>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t("policies.table.name")}</TableHeaderCell>
                <TableHeaderCell>{t("policies.table.steps")}</TableHeaderCell>
                <TableHeaderCell>{t("policies.table.cycles")}</TableHeaderCell>
                <TableHeaderCell>{t("policies.table.monitors")}</TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {policies.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium text-gray-900">{p.name}</TableCell>
                  <TableCell className="text-gray-500">
                    {t("policies.table.step", { count: p.steps?.length ?? 0 })}
                  </TableCell>
                  <TableCell className="text-gray-500">
                    {p.repeat_count ?? 1}×
                  </TableCell>
                  <TableCell className="text-gray-500">
                    {p.monitor_count ?? 0}
                  </TableCell>
                  <TableCell>
                    {can("on_call.manage") && (
                      <div className="flex items-center gap-2">
                        <Button variant="secondary" size="xs" onClick={() => openEdit(p)}>{t("policies.table.edit")}</Button>
                        <Button
                          variant="secondary"
                          size="xs"
                          color="red"
                          icon={Trash2}
                          disabled={deleting === p.id}
                          onClick={() => handleDelete(p.id, p.name)}
                        />
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {dialogOpen && (
        <EscalationPolicyDialog
          policy={editing}
          members={members}
          groups={groups}
          schedules={schedules}
          onClose={() => setDialogOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function OnCall() {
  const { t } = useTranslation("on_call");
  const can = usePermission();
  const [tab, setTab] = useState("schedules");
  const schedulesQ = useSchedules();
  const membersQ = useMembers();
  const groupsQ = useChannels();

  const schedules = schedulesQ.data ?? [];
  const members = membersQ.data ?? [];
  const groups = groupsQ.data ?? [];
  const schedulesLoading = schedulesQ.isLoading;
  const upgraded = schedulesQ.error?.response?.status !== 402;

  if (!upgraded) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
          <p className="mt-1 text-sm text-gray-500">{t("subtitle")}</p>
        </div>
        <UpgradePrompt />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
        <p className="mt-1 text-sm text-gray-500">{t("subtitle")}</p>
      </div>

      {!schedulesLoading && (
        <CurrentOnCallCard schedules={schedules} members={members} />
      )}

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {Object.entries(t("tabs", { returnObjects: true })).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={[
                "border-b-2 pb-3 text-sm font-medium transition-colors",
                tab === key
                  ? "border-teal-600 text-teal-600"
                  : "border-transparent text-gray-500 hover:text-gray-700",
              ].join(" ")}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {tab === "schedules" && (
        <SchedulesSection
          schedules={schedules}
          loading={schedulesLoading}
          members={members}
          can={can}
        />
      )}
      {tab === "policies" && (
        <EscalationPoliciesSection
          members={members}
          groups={groups}
          schedules={schedules}
          can={can}
        />
      )}
    </div>
  );
}
